/**
 * PublicOfferSign — öffentliche Angebots-Signier-Seite (Iteration 73)
 * ────────────────────────────────────────────────────────────────────
 * Kunde scannt QR-Code auf dem PDF → landet hier. Kein Login.
 *
 * URL: /o/:token
 *
 * Ablauf:
 *   1. Übersicht (Kunde, Firma, Angebots-Nr., Gesamtsumme, KfW-Zuschuss)
 *   2. Namen eingeben, Häkchen für AGB + Widerrufsbelehrung
 *   3. Signatur-Feld → mit Finger unterschreiben
 *   4. Submit → Status wechselt zu „signiert", Bestätigung wird gezeigt
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Loader2, AlertCircle, Gift } from "lucide-react";
import { InlineSignaturePad } from "@/components/InlineSignaturePad";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Formatiert EUR-Beträge deutsch — verwendet dieselbe Konvention wie im PDF
// (Tausenderpunkt, Dezimalkomma, "€"-Suffix).
const eur = (v) => new Intl.NumberFormat("de-DE", {
  style: "currency", currency: "EUR", minimumFractionDigits: 2,
}).format(Number(v || 0));

export default function PublicOfferSign() {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signerName, setSignerName] = useState("");
  const [signature, setSignature] = useState(null);
  const [acceptAgb, setAcceptAgb] = useState(false);
  const [acceptWiderruf, setAcceptWiderruf] = useState(false);
  // Iter89w (2026-02-25) — Umfang der Unterschrift. Beide Optionen
  // vorangekreuzt: der häufigste Fall ist "Angebot annehmen + Vollmacht
  // erteilen in einem Schritt". Kunde kann einzeln abwählen, aber
  // mindestens eine der beiden muss gesetzt bleiben.
  const [acceptOffer, setAcceptOffer] = useState(true);
  const [grantVollmacht, setGrantVollmacht] = useState(true);
  const [claimBonus, setClaimBonus] = useState(false);
  const [referrerName, setReferrerName] = useState("");
  // Iter88zao (2026-02-18) — dynamische Signatur-Optionen (Extra-Checkboxen).
  // Vom Backend geladen: [{id, title, description, checkbox_label, requires_check_to_sign}].
  const [extraOptions, setExtraOptions] = useState([]);
  const [extraChecked, setExtraChecked] = useState({});
  // Iter88zap — separate Signaturen für die 2 Rechtserklärungen
  // (Erlöschen · Vorzeitiger Beginn). Beide optional; wenn leer nutzt der
  // PDF-Renderer die Haupt-Signatur als Fallback.
  const [sigErloeschen, setSigErloeschen] = useState(null);
  const [sigVorzeitig, setSigVorzeitig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [signedNow, setSignedNow] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/offer/${token}`)
      .then((r) => {
        setOffer(r.data);
        // Iter88zao — dynamische Signatur-Optionen initialisieren.
        // Backend liefert bei nicht-signiertem Angebot alle aktiven Optionen,
        // bei bereits signiertem Angebot den Snapshot mit `checked`-Flag.
        const opts = Array.isArray(r.data?.signing_options) ? r.data.signing_options : [];
        setExtraOptions(opts);
        const init = {};
        for (const o of opts) {
          init[o.id || o.option_id] = Boolean(o.checked);
        }
        setExtraChecked(init);
      })
      .catch((e) => setLoadErr(e.response?.data?.detail || "Angebot nicht gefunden."))
      .finally(() => setLoading(false));
  }, [token]);

  // Iteration 92 — konsolidiertes State-Enum vom Backend nutzen. Fallback
  // auf die alten Booleans, falls das Backend noch nicht deployed wurde (z.B.
  // während des Rolling-Deploys auf PROD).
  const state = offer?.state
    || (offer?.signed ? "signed"
        : offer?.revoked ? "revoked"
        : offer?.is_latest_in_project === false ? "outdated"
        : "current");
  const isSigned = state === "signed";
  const isRevoked = state === "revoked";
  const isOutdated = state === "outdated";
  const isCurrent = state === "current";
  // Iter88zao — Pflicht-Optionen: alle mit `requires_check_to_sign=true`
  // müssen angehakt sein, sonst blockiert das Backend die Signatur mit 400.
  const requiredOk = extraOptions
    .filter((o) => o.requires_check_to_sign)
    .every((o) => extraChecked[o.id || o.option_id]);
  // Iter89w — Mindestens eine Umfang-Box muss angekreuzt sein
  const scopeOk = acceptOffer || grantVollmacht;
  const canSubmit = signerName.trim().length >= 2
    && !!signature && acceptAgb && acceptWiderruf && scopeOk && requiredOk && !submitting && isCurrent;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const selected_options = extraOptions.map((o) => ({
        option_id: o.id || o.option_id,
        title: o.title,
        checked: Boolean(extraChecked[o.id || o.option_id]),
      }));
      await axios.post(`${API}/public/offer/${token}/sign`, {
        signer_name: signerName.trim(),
        signature_data_url: signature,
        accept_agb: acceptAgb,
        accept_widerruf: acceptWiderruf,
        accept_offer: acceptOffer,
        grant_vollmacht: grantVollmacht,
        claim_empfehlungsbonus: claimBonus,
        referrer_name: referrerName.trim() || null,
        selected_options,
        signature_erloeschen_data_url: sigErloeschen || null,
        signature_vorzeitiger_beginn_data_url: sigVorzeitig || null,
      });
      setSignedNow(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Signatur fehlgeschlagen — bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1418] flex items-center justify-center text-zinc-300">
        <Loader2 className="animate-spin mr-2" /> Angebot wird geladen …
      </div>
    );
  }
  if (loadErr) {
    return (
      <div className="min-h-screen bg-[#0F1418] flex items-center justify-center p-6">
        <div className="bg-[#1a2126] border border-red-800 rounded-2xl p-6 max-w-lg text-center">
          <AlertCircle className="mx-auto text-red-400" size={40} />
          <h1 className="text-white text-lg font-bold mt-3">Link nicht gültig</h1>
          <p className="text-zinc-400 text-sm mt-2">{String(loadErr)}</p>
          <p className="text-zinc-500 text-xs mt-4">Bitte prüfen Sie den QR-Code oder den Link aus Ihrem Angebots-PDF.</p>
        </div>
      </div>
    );
  }

  const alreadySigned = isSigned;

  return (
    <div className="min-h-screen bg-[#0F1418] text-zinc-100">
      {/* Kopfzeile */}
      <header className="border-b border-zinc-800 bg-[#0B1013]">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <ShieldCheck className="text-emerald-400" size={22} />
          <div>
            <div className="text-[11px] uppercase tracking-widest font-mono text-emerald-400">Digitales Angebot</div>
            <h1 className="text-lg font-bold tracking-tight" data-testid="public-offer-company">{offer.company_name}</h1>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Iter92 — nur noch state-basiert. Alle 3 non-current States
            zeigen Kontext-Info; Signatur ist automatisch blockiert wenn
            state !== 'current'. */}
        {(isOutdated || isRevoked) && (
          <section
            className="bg-red-500/10 border-2 border-red-500/60 rounded-2xl p-4"
            data-testid="public-offer-outdated-warning"
          >
            <div className="flex gap-3">
              <AlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={24} />
              <div className="flex-1 min-w-0">
                <div className="text-red-200 font-bold text-[15px] leading-tight">
                  {isRevoked ? "QR-Code gesperrt" : "Veraltete Version — bitte NICHT unterschreiben"}
                </div>
                <div className="text-red-100 text-[13px] mt-1.5 leading-snug">
                  {isRevoked
                    ? <>Dieses Angebot wurde durch eine neuere Fassung
                        {offer.revoked_by_offer_number ? <> <b className="font-mono">{offer.revoked_by_offer_number}</b></> : null}
                        {" "}abgelöst. Ihr QR-Code ist nicht mehr gültig.</>
                    : <>Dieses Angebot ist <b>Version {offer.version_index}</b> von{" "}
                       <b>{offer.total_offers_in_project}</b>. Ihr Vertriebler hat inzwischen eine
                       {offer.newer_offer_number
                         ? <> neuere Version <b>{offer.newer_offer_number}</b></>
                         : <> aktuellere Version</>}
                       {" "}erstellt.</>}
                </div>
                <div className="text-red-100 text-[12.5px] mt-2 font-medium">
                  Bitte fordern Sie den <b>aktuellen QR-Code</b> bei Ihrem Vertriebler an.
                </div>
              </div>
            </div>
          </section>
        )}
        {isCurrent && offer.total_offers_in_project > 1 && (
          <section
            className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3"
            data-testid="public-offer-latest-notice"
          >
            <div className="flex items-center gap-2 text-emerald-200 text-[13px] font-medium">
              <ShieldCheck size={16} />
              Sie unterschreiben die <b>aktuelle Version {offer.version_index}
              /{offer.total_offers_in_project}</b> · frühere Fassungen sind ungültig.
            </div>
          </section>
        )}

        {/* Übersichts-Karte */}
        <section className="bg-[#1a2126] border border-zinc-800 rounded-2xl p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
            Angebot {offer.offer_number}
          </div>
          <h2 className="text-2xl font-bold mb-4" data-testid="public-offer-customer">
            {offer.customer_name || "Sehr geehrter Kunde"}
          </h2>

          {offer.package_name && (
            <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/30 px-3 py-2 mb-4">
              <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold mb-0.5">Ihr gewähltes Paket</div>
              <div className="text-[15px] font-semibold" data-testid="public-offer-package">{offer.package_name}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-[#0F1418] rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Gesamt (brutto)</div>
              <div className="text-lg font-bold text-white mt-0.5" data-testid="public-offer-gross">{eur(offer.total_gross)}</div>
            </div>
            {offer.kfw_foerderbetrag > 0 && (
              <div className="bg-[#0F1418] rounded-lg p-3">
                <div className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">KfW-Zuschuss</div>
                <div className="text-lg font-bold text-emerald-300 mt-0.5" data-testid="public-offer-kfw">–{eur(offer.kfw_foerderbetrag)}</div>
              </div>
            )}
            {offer.kfw_eigenanteil > 0 && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 col-span-2">
                <div className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold">Ihr Eigenanteil nach Förderung</div>
                <div className="text-2xl font-black text-emerald-300 mt-0.5" data-testid="public-offer-eigenanteil">{eur(offer.kfw_eigenanteil)}</div>
              </div>
            )}
          </div>
        </section>

        {/* Empfehlungsbonus-Card (Iter88za, 17.02.2026) — nur im „current"
            State + wenn im Tenant aktiviert. Kunde kann Bonus geltend machen;
            Sales-Rep muss intern freigeben (2b). Betrag wird als Snapshot
            gespeichert. */}
        {isCurrent && offer.empfehlungsbonus_enabled && offer.empfehlungsbonus_amount_eur > 0 && (
          <section
            className={`rounded-2xl p-5 transition-all ${
              claimBonus
                ? "bg-gradient-to-br from-amber-500/15 to-orange-500/10 border-2 border-amber-400"
                : "bg-[#1a2126] border border-amber-500/40"
            }`}
            data-testid="public-offer-bonus-card"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={claimBonus}
                onChange={(e) => setClaimBonus(e.target.checked)}
                data-testid="public-offer-claim-bonus"
                className="mt-1 h-5 w-5 rounded border-amber-500/60 bg-[#0F1418] text-amber-500 focus:ring-2 focus:ring-amber-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="text-amber-400" size={20} />
                  <h3 className="text-base font-bold text-amber-200">
                    Empfehlungsbonus in Anspruch nehmen · {eur(offer.empfehlungsbonus_amount_eur)}
                  </h3>
                </div>
                <p className="text-[13px] text-zinc-300 leading-snug">
                  {offer.empfehlungsbonus_description}
                </p>
                <p className="text-[11px] text-amber-200/70 mt-1.5 italic">
                  Der Bonus schließt einen ggf. bereits im Angebot enthaltenen Rabatt aus (Entweder-Oder). Freigabe durch unseren Vertrieb nach Prüfung der Empfehlung.
                </p>
              </div>
            </label>
            {claimBonus && (
              <div className="mt-4 pt-4 border-t border-amber-500/30" data-testid="public-offer-bonus-referrer">
                <label className="text-[12px] font-semibold uppercase tracking-wider text-amber-100 block mb-1.5">
                  Von wem wurden Sie empfohlen? <span className="text-zinc-400 font-normal normal-case">(optional)</span>
                </label>
                <input
                  data-testid="public-offer-referrer-input"
                  type="text"
                  value={referrerName}
                  onChange={(e) => setReferrerName(e.target.value)}
                  placeholder="Vor- und Nachname des Empfehlungsgebers"
                  className="w-full h-11 bg-[#0F1418] border border-amber-500/50 rounded-lg px-3 text-white focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
                />
              </div>
            )}
            {claimBonus && offer.kfw_eigenanteil > 0 && (
              <div className="mt-4 pt-4 border-t border-amber-500/30 flex items-center justify-between">
                <span className="text-[12px] uppercase tracking-wider text-amber-100 font-semibold">
                  Ihr Eigenanteil nach Bonus (voraussichtlich)
                </span>
                <span className="text-xl font-black text-amber-200" data-testid="public-offer-bonus-preview">
                  {eur(Math.max(0, offer.kfw_eigenanteil - offer.empfehlungsbonus_amount_eur))}
                </span>
              </div>
            )}
          </section>
        )}

        {/* Signatur-Bereich */}
        {alreadySigned || signedNow ? (
          <section className="bg-emerald-500/10 border-2 border-emerald-500 rounded-2xl p-6 text-center" data-testid="public-offer-signed-block">
            <CheckCircle2 className="mx-auto text-emerald-400" size={56} />
            <h3 className="text-xl font-bold text-emerald-300 mt-3">Angebot signiert</h3>
            <p className="text-zinc-300 text-sm mt-2">
              Vielen Dank! Ihre Zustimmung wurde erfasst. Wir melden uns in Kürze zur Terminierung der Vor-Ort-Aufnahme.
            </p>
            {(claimBonus || offer.empfehlungsbonus_claimed) && (
              <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-left" data-testid="public-offer-bonus-confirmation">
                <div className="flex items-center gap-2 text-amber-200 font-semibold mb-1">
                  <Gift size={16} /> Empfehlungsbonus vermerkt
                </div>
                <p className="text-[12.5px] text-amber-100/90">
                  Sie haben einen Empfehlungsbonus in Höhe von <b>{eur(offer.empfehlungsbonus_amount_eur || offer.empfehlungsbonus_snapshot?.amount_eur || 0)}</b> beantragt. Unser Vertrieb prüft die Empfehlung und gibt den Betrag zeitnah frei — Sie erhalten eine gesonderte E-Mail-Bestätigung.
                </p>
              </div>
            )}
            <p className="text-zinc-500 text-xs mt-3">
              Eine PDF-Kopie mit Ihrer Unterschrift wird Ihnen per E-Mail zugestellt.
            </p>
          </section>
        ) : isOutdated || isRevoked ? (
          <section
            className="bg-[#1a2126] border border-red-500/40 rounded-2xl p-6 text-center"
            data-testid="public-offer-sign-locked"
          >
            <AlertCircle className="mx-auto text-red-400" size={48} />
            <h3 className="text-lg font-bold text-red-200 mt-3">
              {isRevoked ? "QR-Code abgelaufen" : "Signatur nicht möglich"}
            </h3>
            <p className="text-zinc-300 text-sm mt-2 leading-relaxed">
              {isRevoked
                ? <>Ihr Vertriebler hat inzwischen eine aktualisierte Fassung
                    {offer.revoked_by_offer_number ? <> <b className="text-red-200 font-mono">{offer.revoked_by_offer_number}</b></> : null}
                    {" "}erstellt und Ihnen einen neuen QR-Code zugesandt.
                    Bitte scannen Sie den neuen Code oder kontaktieren Sie
                    uns direkt.</>
                : "Dieses Angebot ist eine ältere Version. Bitte kontaktieren Sie Ihren Vertriebler — er sendet Ihnen den QR-Code zur aktuellen Version zu."}
            </p>
          </section>
        ) : (
          <section className="bg-[#1a2126] border border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="public-offer-sign-form">
            <h3 className="text-lg font-bold">Jetzt digital signieren</h3>
            <p className="text-sm text-zinc-400">
              Bitte tragen Sie Ihren Namen ein, bestätigen Sie die Bedingungen und unterschreiben Sie mit dem Finger im Feld unten.
            </p>

            <div>
              <label className="text-[12px] font-semibold uppercase tracking-wider text-zinc-300 block mb-1.5">
                Vollständiger Name
              </label>
              <input
                data-testid="public-offer-name-input"
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Vor- und Nachname"
                className="w-full h-11 bg-[#0F1418] border border-zinc-700 rounded-lg px-3 text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              />
            </div>

            {/* Iter89w — Umfang der Unterschrift. Mindestens eine Box pflicht. */}
            <div
              className="rounded-xl border border-sky-500/40 bg-sky-500/5 p-4 space-y-3"
              data-testid="public-offer-scope"
            >
              <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-300">
                Umfang meiner Unterschrift
                <span className="ml-1 text-sky-400/80 normal-case font-normal">
                  (mindestens eins ankreuzen)
                </span>
              </div>
              <label
                className="flex items-start gap-3 cursor-pointer select-none"
                data-testid="public-offer-scope-offer-row"
              >
                <input
                  type="checkbox"
                  checked={acceptOffer}
                  onChange={(e) => setAcceptOffer(e.target.checked)}
                  data-testid="public-offer-accept-offer"
                  className="mt-1 h-5 w-5 rounded border-sky-500/60 bg-[#0F1418] text-sky-400 focus:ring-2 focus:ring-sky-400"
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-[14.5px] font-semibold text-white">
                    Angebot annehmen
                  </span>
                  <span className="block text-[12.5px] text-zinc-400 mt-0.5 leading-snug">
                    Ich nehme das vorliegende PV-Angebot verbindlich an.
                  </span>
                </span>
              </label>
              <label
                className="flex items-start gap-3 cursor-pointer select-none"
                data-testid="public-offer-scope-vollmacht-row"
              >
                <input
                  type="checkbox"
                  checked={grantVollmacht}
                  onChange={(e) => setGrantVollmacht(e.target.checked)}
                  data-testid="public-offer-grant-vollmacht"
                  className="mt-1 h-5 w-5 rounded border-sky-500/60 bg-[#0F1418] text-sky-400 focus:ring-2 focus:ring-sky-400"
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-[14.5px] font-semibold text-white">
                    Vollmacht erteilen
                  </span>
                  <span className="block text-[12.5px] text-zinc-400 mt-0.5 leading-snug">
                    Ich bevollmächtige {offer.company_name || "die Firma"} zur
                    Anmeldung meiner PV-Anlage beim Netzbetreiber sowie zur
                    Registrierung im Marktstammdatenregister (MaStR).
                  </span>
                </span>
              </label>
              {!scopeOk && (
                <p
                  className="text-[12px] text-amber-400"
                  data-testid="public-offer-scope-hint"
                >
                  Bitte wählen Sie mindestens einen Umfang (Angebot und/oder
                  Vollmacht), bevor Sie unterschreiben.
                </p>
              )}
            </div>

            <div>
              <label className="text-[12px] font-semibold uppercase tracking-wider text-zinc-300 block mb-1.5">
                Unterschrift
              </label>
              <InlineSignaturePad
                onChange={setSignature}
                height={140}
                testId="public-offer-sig"
              />
            </div>

            <div className="space-y-2 pt-1">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptAgb}
                  onChange={(e) => setAcceptAgb(e.target.checked)}
                  data-testid="public-offer-accept-agb"
                  className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-[#0F1418] text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-sm text-zinc-300">
                  Ich akzeptiere die <b>AGB</b> gemäß Angebots-PDF.
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptWiderruf}
                  onChange={(e) => setAcceptWiderruf(e.target.checked)}
                  data-testid="public-offer-accept-widerruf"
                  className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-[#0F1418] text-emerald-500 focus:ring-emerald-500"
                />
                <span className="text-sm text-zinc-300">
                  Ich habe die <b>Widerrufsbelehrung</b> zur Kenntnis genommen.
                </span>
              </label>
            </div>

            {/* Iter88zao — dynamische Zusatz-Checkboxen aus Tenant-Config */}
            {extraOptions.length > 0 && (
              <div className="space-y-3 pt-1 border-t border-zinc-800" data-testid="public-offer-extra-options">
                <div className="pt-3 text-[12px] font-semibold uppercase tracking-wider text-zinc-300">
                  Weitere Optionen
                </div>
                {extraOptions.map((o) => {
                  const oid = o.id || o.option_id;
                  return (
                    <label key={oid} className="flex items-start gap-2 cursor-pointer" data-testid={`public-offer-option-${oid}`}>
                      <input
                        type="checkbox"
                        checked={!!extraChecked[oid]}
                        onChange={(e) => setExtraChecked({ ...extraChecked, [oid]: e.target.checked })}
                        data-testid={`public-offer-option-check-${oid}`}
                        className="mt-0.5 h-4 w-4 rounded border-zinc-600 bg-[#0F1418] text-emerald-500 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-zinc-300 flex-1">
                        <span className="font-semibold text-white block">{o.title}</span>
                        {o.description && (
                          <span className="block text-[12.5px] text-zinc-400 mt-0.5 whitespace-pre-line">{o.description}</span>
                        )}
                        <span className="block text-[12px] text-zinc-500 italic mt-0.5">
                          {o.checkbox_label || "Ich stimme zu"}
                          {o.requires_check_to_sign && <span className="ml-1 text-amber-400 not-italic font-bold">*</span>}
                        </span>
                      </span>
                    </label>
                  );
                })}
                {extraOptions.some((o) => o.requires_check_to_sign) && !requiredOk && (
                  <p className="text-[11.5px] text-amber-400" data-testid="public-offer-required-hint">
                    * Diese Optionen müssen vor der Signatur bestätigt werden.
                  </p>
                )}
              </div>
            )}

            {/* Iter88zap — separate Signaturen für die Rechtserklärungen */}
            <details className="pt-1 border-t border-zinc-800" data-testid="public-offer-extra-sigs">
              <summary className="pt-3 text-[12px] font-semibold uppercase tracking-wider text-zinc-300 cursor-pointer select-none">
                Zusätzliche Erklärungen separat signieren (optional)
              </summary>
              <div className="mt-3 space-y-4">
                <p className="text-[12px] text-zinc-400">
                  Wenn Sie <b>&bdquo;Erklärung zum Erlöschen&ldquo;</b> und <b>&bdquo;Erklärung zum vorzeitigen Beginn&ldquo;</b> separat unterschreiben möchten, nutzen Sie die Felder unten. Andernfalls wird Ihre Haupt-Unterschrift auch für diese Erklärungen verwendet.
                </p>
                <div>
                  <div className="text-[12px] font-semibold text-zinc-200 mb-1.5">Erklärung zum Erlöschen des Widerrufsrechts</div>
                  <InlineSignaturePad onChange={setSigErloeschen} height={110} testId="public-offer-sig-erloeschen" />
                </div>
                <div>
                  <div className="text-[12px] font-semibold text-zinc-200 mb-1.5">Erklärung zum vorzeitigen Beginn</div>
                  <InlineSignaturePad onChange={setSigVorzeitig} height={110} testId="public-offer-sig-vorzeitig" />
                </div>
              </div>
            </details>

            <button
              onClick={submit}
              disabled={!canSubmit}
              data-testid="public-offer-submit"
              className="w-full h-12 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-700 disabled:text-zinc-500 disabled:cursor-not-allowed text-[#0F1418] font-black text-base flex items-center justify-center gap-2 transition"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
              {acceptOffer && grantVollmacht
                ? "Angebot & Vollmacht verbindlich signieren"
                : acceptOffer
                ? "Angebot verbindlich signieren"
                : grantVollmacht
                ? "Vollmacht verbindlich erteilen"
                : "Verbindlich signieren"}
            </button>
            <p className="text-[11px] text-zinc-500 leading-tight text-center">
              Ihre Signatur ist rechtssicher (§126a BGB). Nach der Zustimmung erhalten Sie eine unterschriebene PDF-Kopie per E-Mail.
            </p>
          </section>
        )}
      </main>
    </div>
  );
}
